// Boot-time guard that the catalog (TEMPLATES in @/lib/data) and the
// component dispatchers (GuestVariants in @/components/variants, LAYOUTS
// in @/components/layouts) are in lockstep.
//
// Background: each template row carries a `.variant` and `.layout` string
// that the runtime uses to look up the React component to render. Today
// the dispatcher type is Record<string, …>, so a typo or missing import
// is not caught at compile time — it silently falls back to a default
// variant (editorial / photo-bleed) at the consumer call site, and the
// user just sees the wrong template render.
//
// This module runs at app boot (imported for side effect from main.tsx)
// and verifies every TEMPLATES entry has a matching dispatcher key.
//   • DEV  → throw with a structured list of mismatches, so the developer
//     sees the failure immediately instead of debugging a "why is the
//     wrong template showing" report later.
//   • PROD → console.error a structured warning. We don't throw, because
//     a single bad row would break the whole gallery for every user;
//     the consumer-level fallbacks remain as defense-in-depth.
//
// As a secondary check we also warn (never throw) about orphan dispatcher
// keys — components registered but not referenced by any template. Those
// are dead code candidates.

import { TEMPLATES } from "@/lib/data";
import { GuestVariants } from "@/components/variants";
import { LAYOUTS } from "@/components/layouts";

interface Mismatch {
  templateId: string;
  kind: "variant" | "layout";
  missingKey: string;
}

function findMismatches(): Mismatch[] {
  const out: Mismatch[] = [];
  for (const t of TEMPLATES) {
    if (!(t.variant in GuestVariants)) {
      out.push({ templateId: t.id, kind: "variant", missingKey: t.variant });
    }
    if (!(t.layout in LAYOUTS)) {
      out.push({ templateId: t.id, kind: "layout", missingKey: t.layout });
    }
  }
  return out;
}

function findOrphans(): { variants: string[]; layouts: string[] } {
  const usedVariants = new Set(TEMPLATES.map((t) => t.variant as string));
  const usedLayouts = new Set(TEMPLATES.map((t) => t.layout as string));
  return {
    variants: Object.keys(GuestVariants).filter((k) => !usedVariants.has(k)),
    layouts: Object.keys(LAYOUTS).filter((k) => !usedLayouts.has(k)),
  };
}

function formatMismatches(rows: Mismatch[]): string {
  return rows
    .map((r) => `  • ${r.templateId}: ${r.kind} "${r.missingKey}" has no dispatcher entry`)
    .join("\n");
}

const mismatches = findMismatches();
if (mismatches.length > 0) {
  const header =
    `Catalog contract failed — ${mismatches.length} template(s) reference ` +
    `a variant or layout with no matching dispatcher entry:`;
  const body = formatMismatches(mismatches);
  const hint =
    "\nFix: either add the missing component + dispatcher entry, or " +
    "correct the .variant/.layout value in frontend/src/lib/data.ts.";
  const message = `${header}\n${body}${hint}`;
  if (import.meta.env.DEV) {
    throw new Error(message);
  } else {
    // eslint-disable-next-line no-console
    console.error(message);
  }
}

// Orphans are informational. We never throw or block on them — a recently
// retired template may keep its component file around briefly.
const orphans = findOrphans();
if (orphans.variants.length > 0 || orphans.layouts.length > 0) {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(
      `Catalog contract: dispatcher has unused entries (dead code candidates).\n` +
        (orphans.variants.length
          ? `  variants: ${orphans.variants.join(", ")}\n`
          : "") +
        (orphans.layouts.length
          ? `  layouts:  ${orphans.layouts.join(", ")}\n`
          : ""),
    );
  }
}
